import React from 'react';
import InlineVendorPicker from '../../../components/InlineVendorPicker';

const QuoteLineRow = ({
  line,
  lineIndex,
  isAlternative = false,
  isFirstAlternative = false,
  isLastAlternative = false,
  isOriginalWithAlternatives = false,
  alternativesCount = 0,
  isExpanded = false,
  onToggleExpansion,
  onCreateAlternative,
  selectedLines,
  editingCell,
  editValue,
  editInputRef,
  focusedCell,
  validationErrors,
  saving,
  conditionOptions,
  certificationOptions,
  currencyOptions,
  uomOptions,
  copiedData,
  // Event handlers
  onLineSelectionChange,
  onCellClick,
  onCellKeyDown,
  onEditChange,
  onDropdownChange,
  onEditKeyPress,
  onEditBlur,
  onVendorSelected,
  onVendorCancel,
  onQuickUpdateField,
  onCopyLineData,
  onPasteToLine,
  onCompleteLine,
  onNoQuoteLine,
  onDuplicateLineData,
  onShowTemplates,
  onViewLineMessages,
  onAddRemarks,
  onUploadCertificate,
  onCreateVersion,
  onViewVersionHistory,
  // Helper functions
  getFieldLabel,
  getCellValue,
  getRowDescription,
  announceToScreenReader
}) => {
  const handleToggleExpansionClick = () => {
    if (onToggleExpansion) onToggleExpansion(line.id);
  };

  const handleCheckboxChange = () => {
    const newSelectedLines = new Set(selectedLines);
    if (newSelectedLines.has(line.id)) {
      newSelectedLines.delete(line.id);
      announceToScreenReader(`Deselected line for part ${line.name}`);
    } else {
      newSelectedLines.add(line.id);
      announceToScreenReader(`Selected line for part ${line.name}`);
    }
    onLineSelectionChange(newSelectedLines);
  };

  const handleCellClick = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`Click on field: ${field}, lineId: ${line.id}, lineIndex: ${lineIndex}, value:`, line[field === 'vendor_id' ? 'vendor_name' : field]);
    onCellClick(line.id, field, line[field === 'vendor_id' ? 'vendor_name' : field], lineIndex, field);
  };

  const handleCellKeyDown = (field) => (e) => {
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      e.stopPropagation();
      onCellKeyDown(line.id, field, line[field === 'vendor_id' ? 'vendor_name' : field]);
    }
  };

  const handleQuickUpdate = (field, value) => (e) => {
    e.stopPropagation();
    onQuickUpdateField(line.id, field, value);
  };

  const handleTemplateClick = (e) => {
    e.stopPropagation();
    onShowTemplates(line.id);
  };

  const handleCopyClick = (e) => {
    e.stopPropagation();
    onCopyLineData(line);
  };

  const handlePasteClick = (e) => {
    e.stopPropagation();
    onPasteToLine(line.id);
  };

  const handleCompleteClick = (e) => {
    e.stopPropagation();
    onCompleteLine(line.id);
  };

  const handleNoQuoteClick = (e) => {
    e.stopPropagation();
    onNoQuoteLine(line.id);
  };

  const handleDuplicateClick = (e) => {
    e.stopPropagation();
    onDuplicateLineData(line);
  };

  const handleViewMessagesClick = (e) => {
    e.stopPropagation();
    onViewLineMessages(line);
  };

  const handleAddRemarksClick = (e) => {
    e.stopPropagation();
    onAddRemarks(line);
  };

  const handleUploadCertificateClick = (e) => {
    e.stopPropagation();
    onUploadCertificate(line);
  };

  const handleCreateVersionClick = (e) => {
    e.stopPropagation();
    onCreateVersion(line.id);
  };

  const handleViewVersionHistoryClick = (e) => {
    e.stopPropagation();
    onViewVersionHistory(line.id);
  };

  const isEditing = (field) => editingCell?.lineId === line.id && editingCell?.field === field;
  const isFocused = (field) => focusedCell?.lineIndex === lineIndex && focusedCell?.field === field;
  const hasValidationError = (field) => validationErrors[`${line.id}_${field}`];

  return (
    <tr
      key={line.id}
      role="row"
      className={`
        ${line.state === 'completed' ? 'completed-line' : ''}
        ${selectedLines.has(line.id) ? 'selected-line' : ''}
        ${isAlternative ? 'alternative-row' : ''}
        ${isOriginalWithAlternatives ? 'original-with-alternatives' : ''}
        ${isOriginalWithAlternatives && isExpanded ? 'expanded' : ''}
        ${line.is_orphaned_alt ? 'orphaned-alternative' : ''}
      `}
    >
      {/* Checkbox Cell */}
      <td role="gridcell">
        <span className="sr-only">{getRowDescription(line, lineIndex)}</span>
        <input
          type="checkbox"
          checked={selectedLines.has(line.id)}
          disabled={line.state === 'completed'}
          onChange={handleCheckboxChange}
          aria-label={`Select line for part ${line.name || 'Unknown'}`}
        />
      </td>

      {/* Alternative Indicator / Expansion Control */}
      <td role="gridcell" className="alternative-indicator">
        {isOriginalWithAlternatives && onToggleExpansion && (
          <button
            onClick={handleToggleExpansionClick}
            className="inline-expand-btn"
            aria-label={isExpanded ? 'Collapse alternatives' : `Expand ${alternativesCount} alternatives`}
            aria-expanded={isExpanded}
            title={`${alternativesCount} alternative${alternativesCount !== 1 ? 's' : ''}`}
          >
            {isExpanded ? '▼' : '►'}
          </button>
        )}
        {isAlternative && (
          <span className="alt-badge" aria-label="Alternative part">ALT</span>
        )}
      </td>

      {/* Version Number */}
      <td role="gridcell" className="version-number">
        <span className="version-badge" title={`Version ${line.version_number || 1} of ${line.version_count || 1}`}>
          v{line.version_number || 1}
        </span>
      </td>

      {/* Part Number */}
      <td role="gridcell" className={`part-number ${isAlternative ? 'indented' : ''}`}>
        {isAlternative && (
          <div
            className={`tree-connector ${isFirstAlternative ? 'first-alternative' : ''} ${isLastAlternative ? 'last-alternative' : ''}`}
            aria-hidden="true"
          >
            <span className="connector-dot"></span>
          </div>
        )}
        {line.product_name || 'N/A'}
      </td>

      {/* Description */}
      <td role="gridcell" className="description">{line.name || 'No product selected'}</td>
      
      {/* Quantity */}
      <td role="gridcell" className="quantity">{line.product_qty || 1}</td>

      {/* UoM Cell - Editable */}
      <td
        id={`cell-${lineIndex}-product_uom_id`}
        role="gridcell"
        className={`editable-cell uom-cell ${isFocused('product_uom_id') ? 'cell-focused' : ''}`}
        data-field="product_uom_id"
        tabIndex={0}
        aria-label={`${getFieldLabel('product_uom_id')} for part ${line.name}. Current value: ${getCellValue(line, 'product_uom_id')}. Click to edit.`}
        onClick={handleCellClick('product_uom_id')}
        onKeyDown={handleCellKeyDown('product_uom_id')}
      >
        {isEditing('product_uom_id') ? (
          <>
            <label htmlFor={`uom-select-${line.id}`} className="sr-only">Select unit of measure for part {line.name}</label>
            <select
              id={`uom-select-${line.id}`}
              ref={editInputRef}
              value={editValue || line.product_uom_id || ''}
              onChange={onDropdownChange || onEditChange}
              onKeyDown={e => onEditKeyPress(e, line.id, 'product_uom_id')}
              onBlur={() => onEditBlur(line.id, 'product_uom_id')}
              autoFocus
            >
              <option value="">Select UoM...</option>
              {uomOptions && uomOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </>
        ) : (
          <div className="cell-display">
            {line.product_uom_name || <span className="empty-value">Click to set UoM</span>}
          </div>
        )}
        {hasValidationError('product_uom_id') && (
          <div className="validation-error" role="alert">{hasValidationError('product_uom_id')}</div>
        )}
      </td>

      {/* Vendor Cell */}
      <td
        id={`cell-${lineIndex}-vendor_id`}
        role="gridcell"
        className={`editable-cell ${isFocused('vendor_id') ? 'cell-focused' : ''}`}
        data-field="vendor_name"
        tabIndex={0}
        aria-label={`${getFieldLabel('vendor_id')} for part ${line.name}. Current value: ${getCellValue(line, 'vendor_id')}. Click to edit.`}
        onClick={handleCellClick('vendor_id')}
        onKeyDown={handleCellKeyDown('vendor_id')}
      >
        {isEditing('vendor_id') ? (
          <InlineVendorPicker 
            line={line}
            initialValue={editValue || ''}
            onSelected={onVendorSelected}
            onCancel={onVendorCancel}
          />
        ) : (
          <div className="cell-display" aria-live="polite">
            {line.vendor_name || <span className="empty-value">Click to select vendor</span>}
          </div>
        )}
      </td>

      {/* Condition Cell */}
      <td
        id={`cell-${lineIndex}-condition`}
        role="gridcell"
        className={`editable-cell ${isFocused('condition') ? 'cell-focused' : ''}`}
        data-field="condition"
        tabIndex={0}
        aria-label={`${getFieldLabel('condition')} for part ${line.name}. Current value: ${getCellValue(line, 'condition')}. Click to edit, or use quick buttons.`}
        onClick={handleCellClick('condition')}
        onKeyDown={handleCellKeyDown('condition')}
      >
        {isEditing('condition') ? (
          <>
            <label htmlFor={`condition-select-${line.id}`} className="sr-only">
              Select condition for part {line.name}
            </label>
            <select
              id={`condition-select-${line.id}`}
              ref={editInputRef}
              value={editValue}
              onChange={onDropdownChange || onEditChange}
              onKeyDown={e => onEditKeyPress(e, line.id, 'condition')}
              onBlur={() => onEditBlur(line.id, 'condition')}
              autoFocus
              aria-describedby={`condition-options-${line.id}`}
            >
              <option value="">Select condition...</option>
              {conditionOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <div id={`condition-options-${line.id}`} className="sr-only">
              Available conditions: Factory New, New, New Surplus, Overhauled, Serviceable, As Removed
            </div>
          </>
        ) : (
          <div className="cell-display">
            {line.condition || <span className="empty-value">Click to set condition</span>}
            <div className="quick-buttons" role="group" aria-label="Quick condition buttons">
              <button onClick={handleQuickUpdate('condition', 'FN')} title="Factory New" aria-label="Set condition to Factory New">F</button>
              <button onClick={handleQuickUpdate('condition', 'NE')} title="New" aria-label="Set condition to New">N</button>
              <button onClick={handleQuickUpdate('condition', 'NS')} title="New Surplus" aria-label="Set condition to New Surplus">S</button>
              <button onClick={handleQuickUpdate('condition', 'OH')} title="Overhauled" aria-label="Set condition to Overhauled">O</button>
              <button onClick={handleQuickUpdate('condition', 'SV')} title="Serviceable" aria-label="Set condition to Serviceable">V</button>
            </div>
          </div>
        )}
        {hasValidationError('condition') && (
          <div className="validation-error" role="alert">{hasValidationError('condition')}</div>
        )}
      </td>

      {/* Certification Cell */}
      <td
        id={`cell-${lineIndex}-certification_id`}
        role="gridcell"
        className={`editable-cell ${isFocused('certification_id') ? 'cell-focused' : ''}`}
        data-field="certification_id"
        tabIndex={0}
        aria-label={`${getFieldLabel('certification_id')} for part ${line.name}. Current value: ${getCellValue(line, 'certification_id')}. Click to edit.`}
        onClick={handleCellClick('certification_id')}
        onKeyDown={handleCellKeyDown('certification_id')}
      >
        {isEditing('certification_id') ? (
          <>
            <label htmlFor={`cert-select-${line.id}`} className="sr-only">Select certification for part {line.name}</label>
            <select
              id={`cert-select-${line.id}`}
              ref={editInputRef}
              value={editValue}
              onChange={onDropdownChange || onEditChange}
              onKeyDown={e => onEditKeyPress(e, line.id, 'certification_id')}
              onBlur={() => onEditBlur(line.id, 'certification_id')}
              autoFocus
            >
              <option value="">Select certification...</option>
              {certificationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </>
        ) : (
          <div className="cell-display">
            {line.certification_name || <span className="empty-value">Click to set certification</span>}
          </div>
        )}
        {hasValidationError('certification_id') && (
          <div className="validation-error" role="alert">{hasValidationError('certification_id')}</div>
        )}
      </td>

      {/* Price Cell */}
      <td
        id={`cell-${lineIndex}-price_unit`}
        role="gridcell"
        className={`editable-cell ${isFocused('price_unit') ? 'cell-focused' : ''}`}
        data-field="price_unit"
        tabIndex={0}
        aria-label={`${getFieldLabel('price_unit')} for part ${line.name}. Current value: ${getCellValue(line, 'price_unit')}. Click to edit.`}
        onClick={handleCellClick('price_unit')}
        onKeyDown={handleCellKeyDown('price_unit')}
      >
        {isEditing('price_unit') ? (
          <>
            <label htmlFor={`price-input-${line.id}`} className="sr-only">Enter unit price for part {line.name}</label>
            <input
              id={`price-input-${line.id}`}
              ref={editInputRef}
              type="number"
              step="0.01"
              value={editValue}
              onChange={onDropdownChange || onEditChange}
              onKeyDown={e => onEditKeyPress(e, line.id, 'price_unit')}
              onBlur={() => onEditBlur(line.id, 'price_unit')}
              placeholder="0.00"
              autoFocus
              aria-describedby={`price-help-${line.id}`}
            />
            <div id={`price-help-${line.id}`} className="sr-only">Enter price in dollars, for example 25.99</div>
          </>
        ) : (
          <div className="cell-display">
            {line.price_unit != null ? `${line.currency_symbol || '$'}${parseFloat(line.price_unit).toFixed(2)}` : <span className="empty-value">Click to set price</span>}
          </div>
        )}
        {hasValidationError('price_unit') && (
          <div className="validation-error" role="alert">{hasValidationError('price_unit')}</div>
        )}
      </td>

      {/* Currency Cell */}
      <td
        id={`cell-${lineIndex}-currency_id`}
        role="gridcell"
        className={`editable-cell ${isFocused('currency_id') ? 'cell-focused' : ''}`}
        data-field="currency_id"
        tabIndex={0}
        aria-label={`Currency for part ${line.name}. Current value: ${line.currency_name || 'USD'}. Click to edit.`}
        onClick={(e) => {
          console.log('Currency cell clicked!', {
            lineId: line.id,
            currency_id: line.currency_id,
            currency_name: line.currency_name,
            currencyOptions: currencyOptions,
            isEditing: isEditing('currency_id')
          });
          handleCellClick('currency_id')(e);
        }}
        onKeyDown={handleCellKeyDown('currency_id')}
      >
        {isEditing('currency_id') ? (
          <>
            <label htmlFor={`currency-select-${line.id}`} className="sr-only">Select currency for part {line.name}</label>
            <select
              id={`currency-select-${line.id}`}
              ref={editInputRef}
              value={editValue || line.currency_id || ''}
              onChange={onDropdownChange || onEditChange}
              onKeyDown={e => onEditKeyPress(e, line.id, 'currency_id')}
              onBlur={() => onEditBlur(line.id, 'currency_id')}
              autoFocus
              aria-describedby={`currency-help-${line.id}`}
            >
              {currencyOptions && currencyOptions.length > 0 ? (
                currencyOptions.map(curr => (
                  <option key={curr.value} value={curr.value}>
                    {curr.label}
                  </option>
                ))
              ) : (
                <option value="1">USD - US Dollar</option>
              )}
            </select>
            <div id={`currency-help-${line.id}`} className="sr-only">Select the currency for this line item</div>
          </>
        ) : (
          <div className="cell-display">
            {line.currency_name || 'USD'}
          </div>
        )}
        {hasValidationError('currency_id') && (
          <div className="validation-error" role="alert">{hasValidationError('currency_id')}</div>
        )}
      </td>

      {/* Lead Time Cell */}
      <td
        id={`cell-${lineIndex}-lead_time`}
        role="gridcell"
        className={`editable-cell ${isFocused('lead_time') ? 'cell-focused' : ''}`}
        data-field="lead_time"
        tabIndex={0}
        aria-label={`${getFieldLabel('lead_time')} for part ${line.name}. Current value: ${getCellValue(line, 'lead_time')}. Click to edit, or use quick buttons.`}
        onClick={handleCellClick('lead_time')}
        onKeyDown={handleCellKeyDown('lead_time')}
      >
        {isEditing('lead_time') ? (
          <>
            <label htmlFor={`leadtime-input-${line.id}`} className="sr-only">Enter lead time in days for part {line.name}</label>
            <input
              id={`leadtime-input-${line.id}`}
              ref={editInputRef}
              type="number"
              value={editValue}
              onChange={onDropdownChange || onEditChange}
              onKeyDown={e => onEditKeyPress(e, line.id, 'lead_time')}
              onBlur={() => onEditBlur(line.id, 'lead_time')}
              placeholder="Days"
              autoFocus
              aria-describedby={`leadtime-help-${line.id}`}
            />
            <div id={`leadtime-help-${line.id}`} className="sr-only">Enter number of days, for example 14 for two weeks</div>
          </>
        ) : (
          <div className="cell-display">
            {line.lead_time ? `${line.lead_time} days` : <span className="empty-value">0 days</span>}
            <div className="quick-buttons" role="group" aria-label="Quick lead time buttons">
              <button onClick={handleQuickUpdate('lead_time', 7)} title="1 week" aria-label="Set lead time to 7 days">7</button>
              <button onClick={handleQuickUpdate('lead_time', 14)} title="2 weeks" aria-label="Set lead time to 14 days">14</button>
              <button onClick={handleQuickUpdate('lead_time', 30)} title="1 month" aria-label="Set lead time to 30 days">30</button>
            </div>
          </div>
        )}
        {hasValidationError('lead_time') && (
          <div className="validation-error" role="alert">{hasValidationError('lead_time')}</div>
        )}
      </td>

      {/* Status */}
      <td role="gridcell" className={`status ${line.state}`}>
        <span aria-label={`Status: ${line.state_display || line.state}`}>
          {line.state_display || line.state}
        </span>
        {saving && editingCell?.lineId === line.id && (
          <span className="saving-indicator" aria-label="Saving changes">💾</span>
        )}
      </td>

      {/* Actions */}
      <td role="gridcell" className="actions">
        <div className="action-buttons" role="group" aria-label={`Actions for part ${line.name}`}>
          {line.state !== 'completed' && (
            <>
              <button
                onClick={handleTemplateClick}
                title="Apply Template"
                className="action-btn template-btn"
                aria-label={`Apply template to part ${line.name}`}
              >
                📝
              </button>
              <button
                onClick={handleCopyClick}
                title="Copy Line (Ctrl+C)"
                className="action-btn copy-btn"
                aria-label={`Copy data from part ${line.name}`}
              >
                📋
              </button>
              <button
                onClick={handlePasteClick}
                title="Paste (Ctrl+V)"
                className="action-btn paste-btn"
                disabled={!copiedData}
                aria-label={`Paste data to part ${line.name}`}
              >
                📄
              </button>
              <button
                onClick={handleDuplicateClick}
                title="Duplicate Line (Ctrl+D)"
                className="action-btn duplicate-btn"
                aria-label={`Duplicate part ${line.name}`}
              >
                📑
              </button>
            </>
          )}

          {/* Version buttons - always visible */}
          <button
            onClick={handleCreateVersionClick}
            title="Create New Version"
            className="action-btn version-btn"
            aria-label={`Create new version for part ${line.name}`}
          >
            🔄
          </button>
          <button
            onClick={handleViewVersionHistoryClick}
            title="View Version History"
            className="action-btn version-history-btn"
            aria-label={`View version history for part ${line.name}`}
          >
            📚
          </button>

          {line.state !== 'completed' && (
            <>
              <button
                onClick={handleAddRemarksClick}
                title="Add Remarks"
                className="action-btn remarks-btn"
                aria-label={`Add remarks for part ${line.name}`}
              >
                📝
              </button>
              <button
                onClick={handleUploadCertificateClick}
                title="Upload Certificate"
                className="action-btn certificate-btn"
                aria-label={`Upload certificate for part ${line.name}`}
              >
                📄
              </button>
              {line.vendor_id && line.price_unit && (
                <button
                  onClick={handleCompleteClick}
                  title="Complete Line (Ctrl+Shift+C)"
                  className="action-btn complete-btn"
                  aria-label={`Mark part ${line.name} as complete`}
                >
                  ✓
                </button>
              )}
              <button
                onClick={handleNoQuoteClick}
                title="No Quote - Mark as unavailable"
                className="action-btn no-quote-btn"
                aria-label={`Mark part ${line.name} as No Quote`}
              >
                ∅
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default React.memo(QuoteLineRow);